import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CompareArrowsOutlined as ChangesIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  EditOutlined as EditIcon,
  OpenInNew as OpenIcon,
  VisibilityOutlined as PreviewIcon,
  WorkOutline as ApplicationIcon,
} from '@mui/icons-material';
import { tailoredApi } from '../../services/api';
import { TailoredVersion } from '../../types';
import { errorMessage } from '../../utils/errors';
import { money } from '../../utils/costs';
import { formatDate, formatDateTime } from '../../utils/format';
import TailoredEditor from './TailoredEditor';

interface Props {
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  /** Only the versions attached to this application; everything recent otherwise. */
  applicationId?: string;
  /** Rendered inside another card: no card of its own and nothing when the list is empty. */
  bare?: boolean;
}

const forJob = (version: TailoredVersion): string =>
  [version.job_title, version.company_name].filter(Boolean).join(' at ') || 'a job';

function outcome(version: TailoredVersion): string {
  const s = version.summary;
  const parts = [`${s.reworded} of ${s.bullets} lines reworded`];
  if (s.restored) parts.push(`${s.restored} put back by the fact check`);
  return parts.join(', ');
}

/**
 * The resumes JobFlo tailored for jobs: what each was for, how it went, the
 * file, and the reworded lines beside the user's own wording.
 * `?tailored=<id>` in the address opens that version's changes, and with
 * `&edit=1` its editor (the extension links here).
 */
const OUTCOME_LABELS: Record<string, string> = {
  interview_scheduled: 'interview',
  interviewed: 'interviewed',
  offer_received: 'offer',
  offer_accepted: 'offer accepted',
};

// How many rows the full list shows before "Show all".
const SHOWN_AT_FIRST = 8;

const TailoredVersions: React.FC<Props> = ({ notify, applicationId, bare = false }) => {
  const [versions, setVersions] = useState<TailoredVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<TailoredVersion | null>(null);
  const [editing, setEditing] = useState<TailoredVersion | null>(null);
  const [preview, setPreview] = useState<{ version: TailoredVersion; url: string | null; error: string | null } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<TailoredVersion | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [filter, setFilter] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const linked = bare ? null : searchParams.get('tailored');
  const linkedEdit = linked !== null && searchParams.get('edit') === '1';

  const load = useCallback(async () => {
    try {
      setVersions(await tailoredApi.list(applicationId ? { application: applicationId } : undefined));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the tailored resumes.'));
    }
  }, [applicationId]);

  useEffect(() => {
    load();
  }, [load]);

  const show = useCallback(
    async (id: string) => {
      try {
        setOpen(await tailoredApi.get(id));
      } catch (e) {
        notify(errorMessage(e, 'Could not load that tailored resume.'), 'error');
      }
    },
    [notify]
  );

  // The editor needs the version's document, which only the detail carries.
  const edit = useCallback(
    async (id: string) => {
      try {
        setOpen(null);
        setEditing(await tailoredApi.get(id));
      } catch (e) {
        notify(errorMessage(e, 'Could not load that tailored resume.'), 'error');
      }
    },
    [notify]
  );

  useEffect(() => {
    if (!linked) return;
    if (linkedEdit) edit(linked);
    else show(linked);
  }, [linked, linkedEdit, show, edit]);

  const unlink = () => {
    if (!linked) return;
    const next = new URLSearchParams(searchParams);
    next.delete('tailored');
    next.delete('edit');
    setSearchParams(next, { replace: true });
  };

  const close = () => {
    setOpen(null);
    unlink();
  };

  const closeEditor = () => {
    setEditing(null);
    unlink();
  };

  const download = async (version: TailoredVersion) => {
    try {
      await tailoredApi.download(version);
    } catch (e) {
      notify(errorMessage(e, 'Could not download the file.'), 'error');
    }
  };

  // The PDF itself, shown in the page. The object URL is released when the preview closes.
  const openPreview = async (version: TailoredVersion) => {
    setPreview({ version, url: null, error: null });
    try {
      const url = await tailoredApi.fileUrl(version);
      setPreview((current) => (current && current.version.id === version.id ? { ...current, url } : (URL.revokeObjectURL(url), current)));
    } catch (e) {
      setPreview((current) => (current && current.version.id === version.id ? { ...current, error: errorMessage(e, 'The file could not be loaded.') } : current));
    }
  };
  const closePreview = () => {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  };

  // Saved: the list shows it as edited, and the new PDF is shown straight away.
  const saved = (version: TailoredVersion) => {
    setVersions((list) => (list ? list.map((item) => (item.id === version.id ? { ...item, ...version } : item)) : list));
    closeEditor();
    notify('Saved. The PDF was made again from your changes.');
    openPreview(version);
  };

  const remove = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await tailoredApi.remove(pendingDelete.id);
      setVersions((list) => (list ? list.filter((item) => item.id !== pendingDelete.id) : list));
      notify('Tailored resume removed.');
      setPendingDelete(null);
    } catch (e) {
      notify(errorMessage(e, 'It could not be removed.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const needle = filter.trim().toLowerCase();
  const matching = useMemo(
    () => (versions ?? []).filter((version) => !needle || `${version.job_title} ${version.company_name}`.toLowerCase().includes(needle)),
    [versions, needle]
  );
  const rows = showAll || bare || needle ? matching : matching.slice(0, SHOWN_AT_FIRST);

  if (bare && (!versions || versions.length === 0)) return null;

  const statusCell = (version: TailoredVersion) => {
    if (version.status === 'building') return <Chip size="small" label={version.step_label || 'Building'} />;
    if (version.status === 'failed') {
      return (
        <Tooltip title={version.error}>
          <Chip size="small" color="error" variant="outlined" label="Not built" />
        </Tooltip>
      );
    }
    return (
      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
        <Tooltip title={`${outcome(version)} · ${version.model}${version.key_source === 'own' ? ' (your key)' : ''}, ${money(version.cost_usd)}`}>
          <Chip size="small" variant="outlined" label={`${version.summary.reworded} of ${version.summary.bullets} reworded`} />
        </Tooltip>
        {version.edited_at && (
          <Tooltip title={`You changed it yourself on ${formatDateTime(version.edited_at)}`}>
            <Chip size="small" variant="outlined" label="Edited by you" />
          </Tooltip>
        )}
        {version.advanced_at ? (
          <Tooltip title="The application went further than a submission, so this resume is kept as an example of one that got through.">
            <Chip size="small" color="success" label={`Kept: ${OUTCOME_LABELS[version.outcome] ?? version.outcome}`} />
          </Tooltip>
        ) : version.outcome === 'rejected' ? (
          <Chip size="small" variant="outlined" label="Rejected" />
        ) : (
          version.submitted_at && <Chip size="small" color="success" variant="outlined" label="Sent" />
        )}
      </Stack>
    );
  };

  const list = (
    <>
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {versions && versions.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          None yet. On an application form, choose "Fill with tailored resume" in the JobFlo panel and the resume built
          for that job appears here.
        </Typography>
      )}
      {versions && versions.length > 0 && (
        <>
          {!bare && versions.length > 5 && (
            <TextField
              size="small"
              label="Find by job or company"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              sx={{ mb: 1.5, width: { xs: '100%', sm: 320 } }}
            />
          )}
          <TableContainer>
            <Table size="small" aria-label="Tailored resumes" sx={{ minWidth: 560 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Job</TableCell>
                  <TableCell>Built</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((version) => (
                  <TableRow key={version.id} hover>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
                        {version.job_title || 'A job'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                        {version.company_name}
                        {version.job_url && (
                          <>
                            {' '}
                            <Link href={version.job_url} target="_blank" rel="noopener noreferrer" aria-label={`Open the posting for ${forJob(version)}`}>
                              <OpenIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} />
                            </Link>
                          </>
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title={formatDateTime(version.created_at)}>
                        <span>{formatDate(version.created_at)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{statusCell(version)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {version.status === 'ready' && (
                        <>
                          <Tooltip title="Preview the PDF">
                            <IconButton size="small" aria-label={`Preview the resume for ${forJob(version)}`} onClick={() => openPreview(version)}>
                              <PreviewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="What changed">
                            <IconButton size="small" aria-label={`What changed for ${forJob(version)}`} onClick={() => show(version.id)}>
                              <ChangesIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton size="small" aria-label={`Edit the resume for ${forJob(version)}`} onClick={() => edit(version.id)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Download">
                            <IconButton size="small" aria-label={`Download the resume for ${forJob(version)}`} onClick={() => download(version)}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!applicationId && version.application && (
                            <Tooltip title="Open the application">
                              <IconButton size="small" component={RouterLink} to={`/applications/${version.application}`} aria-label={`Open the application for ${forJob(version)}`}>
                                <ApplicationIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                      {version.status !== 'building' && (
                        <Tooltip title="Remove">
                          <IconButton size="small" aria-label={`Remove the resume for ${forJob(version)}`} onClick={() => setPendingDelete(version)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {needle && matching.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Nothing matches "{filter.trim()}".
            </Typography>
          )}
          {!bare && !needle && matching.length > rows.length && (
            <Button size="small" onClick={() => setShowAll(true)} sx={{ mt: 1 }}>
              Show all {matching.length}
            </Button>
          )}
        </>
      )}
    </>
  );

  return (
    <>
      {bare ? (
        list
      ) : (
        <Card variant="outlined" component="section" aria-labelledby="tailored-versions-title">
          <CardContent>
            <Typography id="tailored-versions-title" variant="h6" fontWeight={600}>
              Tailored resumes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your resume, reordered and reworded for one job, drawing also on your Experience library. Every line is
              checked against what you wrote: a line that says anything your resume and library do not is put back to your
              wording. You can edit any of them yourself, and the PDF is made again. Each is removed 30 days after it was
              built, unless the application went further than a submission: those are kept.
            </Typography>
            {list}
          </CardContent>
        </Card>
      )}

      <Dialog open={preview !== null} onClose={closePreview} fullWidth maxWidth="md" aria-labelledby="tailored-preview-title">
        <DialogTitle id="tailored-preview-title" sx={{ pr: 6 }}>
          {preview ? `Resume for ${forJob(preview.version)}` : ''}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, height: '75vh', display: 'flex', flexDirection: 'column' }}>
          {preview?.error && <Alert severity="error" sx={{ m: 2 }}>{preview.error}</Alert>}
          {preview && !preview.error && !preview.url && (
            <Typography variant="body2" color="text.secondary" sx={{ m: 2 }}>
              Loading the PDF...
            </Typography>
          )}
          {preview?.url && (
            <Box component="iframe" src={preview.url} title={`PDF preview: resume for ${forJob(preview.version)}`} sx={{ flex: 1, width: '100%', border: 0 }} />
          )}
          {preview?.url && (
            <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 0.75 }}>
              If the preview stays blank, your browser cannot show PDFs inline: use Download or Open in a new tab.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {preview?.url && (
            <Button startIcon={<OpenIcon />} component="a" href={preview.url} target="_blank" rel="noopener noreferrer">
              Open in a new tab
            </Button>
          )}
          {preview && (
            <Button
              startIcon={<EditIcon />}
              onClick={() => {
                const id = preview.version.id;
                closePreview();
                edit(id);
              }}
            >
              Edit
            </Button>
          )}
          {preview && (
            <Button startIcon={<DownloadIcon />} onClick={() => download(preview.version)}>
              Download
            </Button>
          )}
          <Button onClick={closePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pendingDelete !== null} onClose={deleting ? undefined : () => setPendingDelete(null)} maxWidth="xs" aria-labelledby="tailored-delete-title">
        <DialogTitle id="tailored-delete-title">Remove this tailored resume?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            The resume for {pendingDelete ? forJob(pendingDelete) : ''} and its file are deleted. An application it was sent
            with keeps its record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={remove} disabled={deleting}>
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={open !== null} onClose={close} fullWidth maxWidth="md" aria-labelledby="tailored-changes-title">
        <DialogTitle id="tailored-changes-title">What changed for {open ? forJob(open) : ''}</DialogTitle>
        <DialogContent dividers>
          {open && open.status !== 'ready' && <Alert severity="info">{open.status === 'failed' ? open.error : 'This resume is still being built.'}</Alert>}
          {open && open.status === 'ready' && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {outcome(open)}. The order of bullets and skills may also differ from your resume, and lines may come from
                your Experience library. Jobs, titles, dates and education are always taken from your resume as they are.
                {open.job_url && (
                  <>
                    {' '}
                    <Link href={open.job_url} target="_blank" rel="noopener noreferrer">
                      Open the posting
                    </Link>
                  </>
                )}
              </Typography>
              {(open.changes ?? []).length === 0 && <Typography variant="body2">No line was reworded; only the order changed.</Typography>}
              {(open.changes ?? []).map((change, index) => (
                <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {change.kind === 'summary' ? 'Your summary' : change.library ? 'Your Experience library' : 'Your resume'}
                    </Typography>
                    <Typography variant="body2" color={change.before ? 'text.primary' : 'text.secondary'}>
                      {change.before || (change.kind === 'summary' ? 'Your resume has no summary.' : 'Not on your resume: a line you added.')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {change.by === 'you' ? 'Written by you' : change.kind === 'summary' ? 'Summary written for this job' : 'Tailored'}
                    </Typography>
                    <Typography variant="body2">{change.after}</Typography>
                    {change.for && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                        Answers: {change.for}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {open && open.status === 'ready' && (
            <Button startIcon={<EditIcon />} onClick={() => edit(open.id)}>
              Edit
            </Button>
          )}
          {open && open.status === 'ready' && (
            <Button startIcon={<DownloadIcon />} onClick={() => download(open)}>
              Download
            </Button>
          )}
          <Button onClick={close}>Close</Button>
        </DialogActions>
      </Dialog>

      <TailoredEditor version={editing} onClose={closeEditor} onSaved={saved} />
    </>
  );
};

export default TailoredVersions;
